/**
 * Pluggable API Service for AI Stylist
 * Uses Picsart Magic Flow Webhooks for AI generation
 */

import { HairColor } from '@/constants/enums';
import { APP_WEBHOOK_URL, MINIAPP_PACKAGE_ID, WEBHOOK_IDS } from '@/constants/miniapp';
import { POLLING } from '@/constants/timings';
import { getContext, isContextAvailable } from '@/miniappContext';
import { setAppStore } from '@/store';
import type { RetouchPreset } from '@/types';
import { isCdnUrl, stripOptimizationParams } from '@/utils/optimizeImageUrl';

import { uploadToCdn, uploadLocalUrlToCdn, uploadBlobToCdn, isLocalUrl } from './cdnService';
import {
  ASPECT_RATIO_PRESERVATION_PROMPT,
  formatRetouchInstruction,
  IDENTITY_PRESERVATION_PROMPT,
  KEEP_BACKGROUND_PROMPT,
  KEEP_HAIR_COLOR_PROMPT,
  KEEP_HAIRSTYLE_PROMPT,
  KEEP_POSE_PROMPT,
  QUALITY_FINISHER,
} from './prompts/virtualTryOn';

// ============================================================================
// CONFIGURATION
// ============================================================================

const WEBHOOK_BASE_URL = `${APP_WEBHOOK_URL}/workflows/magic-flow-webhook`;
const WEBHOOK_SUBMIT_URL = `${WEBHOOK_BASE_URL}/submit`;
const WEBHOOK_CALCULATOR_URL = `${APP_WEBHOOK_URL}/workflows/magic-flow-webhook-calculator/execute`;

// IMAGE PROCESSING

const isRelativePath = (url: string): boolean => {
  return url.startsWith('/') && !url.startsWith('//');
};

/**
 * Process an image URL - upload to CDN if it's base64, localhost, or relative path
 * Returns a publicly accessible URL
 */
const processImageUrl = async (imageUrl: string): Promise<string> => {
  if (isCdnUrl(imageUrl)) {
    return stripOptimizationParams(imageUrl);
  }

  if (isRelativePath(imageUrl)) {
    const fullUrl = `${window.location.origin}${imageUrl}`;
    return uploadLocalUrlToCdn(fullUrl);
  }

  if (isLocalUrl(imageUrl)) {
    return uploadLocalUrlToCdn(imageUrl);
  }

  if (imageUrl.startsWith('data:')) {
    return uploadToCdn(imageUrl);
  }

  if (imageUrl.startsWith('blob:')) {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    return uploadBlobToCdn(blob);
  }

  return imageUrl;
};

// ============================================================================
// RESULT TYPES
// ============================================================================

export interface ImageGenerationResult {
  success: boolean;
  imageUrl?: string;
  driveUid?: string;
  error?: string;
}

export interface ChatCompletionResult {
  success: boolean;
  text?: string;
  error?: string;
}

// ============================================================================
// WEBHOOK TYPES
// ============================================================================

type WebhookSubmitResponse = {
  status: string;
  reason: string;
  response: {
    id: string;
  };
};

type PollStatus = 'COMPLETED' | 'FAILED' | 'PENDING' | 'PROCESSING';

type PollResultItem = {
  type: string;
  driveUid?: string;
  result: {
    url: string;
    metadata?: {
      width: number;
      height: number;
    };
  };
};

type PollResponse = {
  response: {
    status: PollStatus;
    output?: {
      url: string;
    };
    result?: PollResultItem[];
  };
};

// ============================================================================
// ERROR HANDLING
// ============================================================================

const parseApiError = (error: unknown): string => {
  if (error instanceof Error) {
    const message = error.message;

    if (message.includes('13') || message.includes('INTERNAL')) {
      return 'Server error. Please try again in a moment.';
    }
    if (message.includes('14') || message.includes('UNAVAILABLE')) {
      return 'Service temporarily unavailable. Wait a few minutes and retry.';
    }
    if (message.includes('RESOURCE_EXHAUSTED') || message.includes('quota')) {
      return 'API quota exceeded. Wait a few minutes before trying again.';
    }
    if (message.includes('network') || message.includes('fetch') || message.includes('timeout')) {
      return 'Network connection issue. Check your internet connection and retry.';
    }
    if (message.includes('INVALID_ARGUMENT')) {
      return 'Invalid input. Try simplifying your prompt.';
    }
    if (message.includes('safety') || message.includes('blocked') || message.includes('SAFETY')) {
      return 'Content blocked by safety filters. Please revise your prompt.';
    }
    if (message.includes('PERMISSION_DENIED')) {
      return 'API permission denied. Verify your API key.';
    }
    if (message.includes('Polling timeout')) {
      return 'Generation took too long. Please try again.';
    }

    return message;
  }

  return String(error);
};

// ============================================================================
// WEBHOOK SERVICE
// ============================================================================

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

class PluggableApiService {
  private static instance: PluggableApiService | null = null;

  private constructor() {}

  public static getInstance(): PluggableApiService {
    if (!PluggableApiService.instance) {
      PluggableApiService.instance = new PluggableApiService();
    }
    return PluggableApiService.instance;
  }

  public static isAvailable(): boolean {
    return isContextAvailable();
  }

  private getFetch() {
    return getContext().utils.api.fetch;
  }

  /**
   * Submit a webhook and return the execution ID
   */
  private async submitWebhook(webhookId: string, params: Record<string, unknown>, driveFolder?: string): Promise<string> {
    const apiFetch = this.getFetch();

    const requestParams: Record<string, unknown> = { webhookId, params };
    if (driveFolder) {
      requestParams.driveOptions = {
        folderName: driveFolder,
        packageId: MINIAPP_PACKAGE_ID,
      };
    }

    const response = await apiFetch(WEBHOOK_SUBMIT_URL, {
      method: 'POST',
      body: JSON.stringify({ params: requestParams }),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const body = await response.text().catch(() => '');
      throw new Error(`Webhook submission failed: ${body || response.statusText}`);
    }

    const data: WebhookSubmitResponse = await response.json();

    if (!data?.response?.id) {
      throw new Error('Webhook response missing id field');
    }

    return data.response.id;
  }

  /**
   * Poll for webhook result until completed or failed
   */
  private async pollUntilCompleted(executionId: string, onProgress?: () => void): Promise<PollResponse> {
    const apiFetch = this.getFetch();
    const pollUrl = `${WEBHOOK_BASE_URL}/${executionId}/result`;

    let attempts = 0;
    let currentInterval = POLLING.INITIAL_INTERVAL;
    let pollingResult: PollResponse | undefined;

    do {
      await sleep(currentInterval);

      const response = await apiFetch(pollUrl, {
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        const body = await response.text().catch(() => '');
        throw new Error(`Poll request failed: ${response.status} ${body || response.statusText}`);
      }

      pollingResult = await response.json();
      attempts++;
      currentInterval = Math.min(currentInterval * POLLING.BACKOFF_FACTOR, POLLING.MAX_INTERVAL);
      onProgress?.();
    } while (attempts < POLLING.MAX_ATTEMPTS && pollingResult!.response.status !== 'COMPLETED' && pollingResult!.response.status !== 'FAILED');

    if (pollingResult!.response.status === 'FAILED') {
      throw new Error(`Workflow execution failed: ${JSON.stringify(pollingResult!.response)}`);
    }

    if (attempts >= POLLING.MAX_ATTEMPTS && pollingResult!.response.status !== 'COMPLETED') {
      throw new Error('Polling timeout: Maximum attempts reached');
    }

    return pollingResult!;
  }

  /**
   * Extract image URL from poll response
   */
  private extractImageUrl(pollResponse: PollResponse): string {
    const result = pollResponse.response.result;
    if (result && result.length > 0) {
      const firstResult = result[0];
      if (firstResult.result?.url) {
        return firstResult.result.url;
      }
    }

    if (pollResponse.response.output?.url) {
      return pollResponse.response.output.url;
    }

    throw new Error('No image URL found in webhook response');
  }

  /**
   * Extract Drive file UID from poll response (present when the webhook was
   * submitted with driveOptions.folderName — the workflow auto-saves the
   * generated image to Drive and returns its uid). Used for later deletion.
   */
  private extractDriveUid(pollResponse: PollResponse): string | undefined {
    const result = pollResponse.response.result;
    if (result && result.length > 0) {
      return result[0].driveUid;
    }
    return undefined;
  }

  /**
   * Extract text value from poll response (for text generation webhooks)
   */
  private extractTextValue(pollResponse: PollResponse): string {
    const result = pollResponse.response.result;
    if (result && result.length > 0) {
      const textResult = result.find(r => r.type === 'text');
      if (textResult?.result && 'value' in textResult.result) {
        return (textResult.result as unknown as { value: string }).value;
      }
    }

    throw new Error('No text found in webhook response');
  }

  // ============================================================================
  // THUMBNAIL PROCESSING (flat-lay conversion)
  // ============================================================================

  async processThumbnail(params: { imageUrl: string; onProgress?: () => void }): Promise<ImageGenerationResult> {
    try {
      const imageUrl = await processImageUrl(params.imageUrl);

      const webhookParams = {
        image: { url: imageUrl },
      };

      const executionId = await this.submitWebhook(WEBHOOK_IDS.WARDROBE_THUMBNAIL, webhookParams);
      const pollResponse = await this.pollUntilCompleted(executionId, params.onProgress);
      const resultUrl = this.extractImageUrl(pollResponse);

      return { success: true, imageUrl: resultUrl };
    } catch (error) {
      console.error('[PluggableAPI] Thumbnail processing error:', error);
      return { success: false, error: parseApiError(error) };
    }
  }

  // ============================================================================
  // OUTFIT GENERATION HELPERS
  // ============================================================================

  /** Maximum number of wardrobe item images sent to the webhook. */
  private static readonly MAX_WARDROBE_IMAGES = 8;

  /** Ordinal labels for referencing images by position in the prompt. */
  private static readonly ORDINALS = ['first', 'second', 'third', 'fourth', 'fifth', 'sixth', 'seventh', 'eighth', 'ninth', 'tenth', 'eleventh', 'twelfth', 'thirteenth'];

  /**
   * Build webhook params for a simple prompt + single character image.
   * Used by virtualTryOn where the caller provides a raw prompt string.
   */
  private async buildSimpleWebhookParams(characterImage: string, prompt: string): Promise<Record<string, unknown>> {
    return {
      prompt: { value: prompt },
      character_image: { url: characterImage },
    };
  }

  /**
   * Build both the prompt and webhook params together in a single pass.
   *
   * Webhook param keys use the original named convention (character_image,
   * style_image, etc.). The prompt references images by ordinal position
   * ("the first image", "the second image") matching the order images appear
   * in the params. Building both together guarantees the ordinals never drift.
   */
  private async buildGenerationPayload(params: {
    characterImage: string;
    wardrobeItems: Array<{ name: string; category: string; imageUrl?: string; prompt?: string; isUserUploaded?: boolean }>;
    gender: string;
    style?: { name: string; description?: string; referenceUrl?: string };
    pose?: { prompt?: string; referenceUrl?: string };
    background?: { prompt?: string; referenceUrl?: string };
    hairstyle?: { description: string; referenceUrl?: string };
    hairColor?: string;
    aspectRatio?: number | null;
    retouchPresets?: Array<{ categoryId: string; presets: RetouchPreset[] }>;
  }): Promise<Record<string, unknown>> {
    const webhookParams: Record<string, unknown> = {};
    const promptParts: string[] = [];
    let imageCount = 0;

    // Helper: register an image param and return its ordinal label for the prompt
    const addImage = (key: string, url: string): string => {
      webhookParams[key] = { url };
      imageCount++;
      return PluggableApiService.ORDINALS[imageCount - 1] || `image ${imageCount}`;
    };

    // --- Character (always first) ---
    const charOrd = addImage('character_image', params.characterImage);
    promptParts.push(`The ${charOrd} image is the character photo of a ${params.gender} person`);

    // --- Style ---
    if (params.style?.referenceUrl) {
      const ord = addImage('style_image', await processImageUrl(params.style.referenceUrl));
      const desc = params.style.description;
      promptParts.push(`The ${ord} image is the style reference — apply ${params.style.name} fashion aesthetic: ${desc}`);
    } else if (params.style) {
      const desc = params.style.description;
      promptParts.push(`Apply ${params.style.name} fashion aesthetic: ${desc}`);
    }

    // --- Pose ---
    if (params.pose?.referenceUrl) {
      const ord = addImage('pose_image', await processImageUrl(params.pose.referenceUrl));
      const desc = params.pose.prompt;
      promptParts.push(`The ${ord} image is the pose reference — transform the pose to: ${desc}`);
    } else if (params.pose) {
      const desc = params.pose.prompt;
      promptParts.push(`Transform the pose to: ${desc}`);
    } else {
      promptParts.push(KEEP_POSE_PROMPT);
    }

    // --- Background ---
    if (params.background?.referenceUrl) {
      const ord = addImage('background_image', await processImageUrl(params.background.referenceUrl));
      const desc = params.background.prompt;
      promptParts.push(`The ${ord} image is the background reference — change the background to: ${desc}`);
    } else if (params.background) {
      const desc = params.background.prompt;
      promptParts.push(`Change the background to: ${desc}`);
    } else {
      promptParts.push(KEEP_BACKGROUND_PROMPT);
    }

    // --- Hairstyle ---
    if (params.hairstyle?.referenceUrl) {
      const ord = addImage('hairstyle_image', await processImageUrl(params.hairstyle.referenceUrl));
      promptParts.push(`The ${ord} image is the hairstyle reference — change hairstyle to: ${params.hairstyle.description}`);
    } else if (params.hairstyle) {
      promptParts.push(`Change hairstyle to: ${params.hairstyle.description}`);
    } else {
      promptParts.push(KEEP_HAIRSTYLE_PROMPT);
    }
    // --- Wardrobe items (capped at MAX_WARDROBE_IMAGES) ---
    const wardrobeWithImages = params.wardrobeItems.filter(item => item.imageUrl).slice(0, PluggableApiService.MAX_WARDROBE_IMAGES);
    if (wardrobeWithImages.length > 0) {
      const itemDescriptions = await Promise.all(
        wardrobeWithImages.map(async (item, i) => {
          const ord = addImage(`wardrobe_image_${i + 1}`, await processImageUrl(item.imageUrl!));
          const desc = item.prompt ? ` — ${item.prompt}` : '';
          return `the ${ord} image is ${item.name} (${item.category})${desc}`;
        }),
      );
      promptParts.push(`Dress the person using the following clothing items: ${itemDescriptions.join('; ')}`);
    }

    // --- Retouch presets (Fun / Looks / Skin / Face / Makeup) ---
    if (params.retouchPresets?.length) {
      let retouchImageIndex = 0;
      for (const { categoryId, presets } of params.retouchPresets) {
        if (!presets.length) continue;
        const descriptions = await Promise.all(
          presets.map(async p => {
            const promptSuffix = p.prompt ? ` — ${p.prompt}` : '';
            if (!p.thumbnail) return `${p.name}${promptSuffix}`;
            retouchImageIndex++;
            const ord = addImage(`retouch_image_${retouchImageIndex}`, await processImageUrl(p.thumbnail));
            return `the ${ord} image is ${p.name}${promptSuffix}`;
          }),
        );
        promptParts.push(`${formatRetouchInstruction(categoryId)}: ${descriptions.join('; ')}`);
      }
    }

    // --- Text-only instructions (no images, no ordinals) ---
    if (params.hairColor && params.hairColor !== HairColor.Natural) {
      promptParts.push(`Change hair color to: ${params.hairColor}`);
    } else {
      promptParts.push(KEEP_HAIR_COLOR_PROMPT);
    }

    promptParts.push(IDENTITY_PRESERVATION_PROMPT);
    promptParts.push(QUALITY_FINISHER);

    if (params.aspectRatio && params.wardrobeItems.some(i => i.isUserUploaded)) {
      promptParts.push(ASPECT_RATIO_PRESERVATION_PROMPT);
    }

    webhookParams.prompt = { value: promptParts.join('. ') + '.' };

    return webhookParams;
  }

  // ============================================================================
  // VIRTUAL TRY-ON (generic prompt + character image, used as fallback)
  // ============================================================================

  async virtualTryOn(params: { characterImageUrl: string; prompt: string; lookFolder: string; onProgress?: () => void }): Promise<ImageGenerationResult> {
    try {
      const characterImage = await processImageUrl(params.characterImageUrl);
      const webhookParams = await this.buildSimpleWebhookParams(characterImage, params.prompt);

      const executionId = await this.submitWebhook(WEBHOOK_IDS.WARDROBE_GENERATE, webhookParams, params.lookFolder);
      const pollResponse = await this.pollUntilCompleted(executionId, params.onProgress);
      const imageUrl = this.extractImageUrl(pollResponse);
      const driveUid = this.extractDriveUid(pollResponse);

      return { success: true, imageUrl, driveUid };
    } catch (error) {
      console.error('[PluggableAPI] Virtual try-on error:', error);
      return { success: false, error: parseApiError(error) };
    }
  }

  // ============================================================================
  // OUTFIT GENERATION (main unified function)
  // ============================================================================

  async generateOutfit(params: {
    characterImageUrl: string;
    wardrobeItems: Array<{ name: string; category: string; imageUrl?: string; prompt?: string; isUserUploaded?: boolean }>;
    gender: string;
    style?: { name: string; description?: string; referenceUrl?: string };
    pose?: { prompt?: string; referenceUrl?: string };
    background?: { prompt?: string; referenceUrl?: string };
    hairstyle?: { description: string; referenceUrl?: string };
    hairColor?: string;
    aspectRatio?: number | null;
    retouchPresets?: Array<{ categoryId: string; presets: RetouchPreset[] }>;
    lookFolder: string;
    onProgress?: () => void;
    onSubmitted?: (executionId: string) => void;
  }): Promise<ImageGenerationResult> {
    try {
      const characterImage = await processImageUrl(params.characterImageUrl);

      const webhookParams = await this.buildGenerationPayload({
        characterImage,
        wardrobeItems: params.wardrobeItems,
        gender: params.gender,
        style: params.style,
        pose: params.pose,
        background: params.background,
        hairstyle: params.hairstyle,
        hairColor: params.hairColor,
        aspectRatio: params.aspectRatio,
        retouchPresets: params.retouchPresets,
      });

      const executionId = await this.submitWebhook(WEBHOOK_IDS.WARDROBE_GENERATE, webhookParams, params.lookFolder);
      params.onSubmitted?.(executionId);
      const pollResponse = await this.pollUntilCompleted(executionId, params.onProgress);
      const imageUrl = this.extractImageUrl(pollResponse);
      const driveUid = this.extractDriveUid(pollResponse);

      return { success: true, imageUrl, driveUid };
    } catch (error) {
      console.error('[PluggableAPI] Generate outfit error:', error);
      return { success: false, error: parseApiError(error) };
    }
  }

  /**
   * Resume polling a previously-submitted execution by id (used after page reload).
   * Returns the same shape as generateOutfit so callers don't need a separate code path.
   */
  async pollExecution(executionId: string, onProgress?: () => void): Promise<ImageGenerationResult> {
    try {
      const pollResponse = await this.pollUntilCompleted(executionId, onProgress);
      const imageUrl = this.extractImageUrl(pollResponse);
      const driveUid = this.extractDriveUid(pollResponse);
      return { success: true, imageUrl, driveUid };
    } catch (error) {
      console.error('[PluggableAPI] pollExecution error:', error);
      return { success: false, error: parseApiError(error) };
    }
  }

  // OUTFIT SUGGESTIONS (text generation)
  async getOutfitSuggestions(params: { characterDescription: string; occasion: string; style: string }): Promise<ChatCompletionResult> {
    try {
      const webhookParams = {
        character_description: { value: params.characterDescription },
        occasion: { value: params.occasion },
        style: { value: params.style },
      };

      const executionId = await this.submitWebhook(WEBHOOK_IDS.WARDROBE_SUGGEST, webhookParams);
      const pollResponse = await this.pollUntilCompleted(executionId);
      const text = this.extractTextValue(pollResponse);

      return { success: true, text };
    } catch (error) {
      console.error('[PluggableAPI] Outfit suggestions error:', error);
      return { success: false, error: parseApiError(error) };
    }
  }
}

// EXPORTS
export const pluggableApiService = PluggableApiService.getInstance();

export const isPluggableApiAvailable = (): boolean => {
  return PluggableApiService.isAvailable();
};

export const calculateWebhook = async (webhookId: string = WEBHOOK_IDS.WARDROBE_GENERATE): Promise<boolean> => {
  try {
    const apiFetch = getContext().utils.api.fetch;

    const response = await apiFetch(WEBHOOK_CALCULATOR_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ params: { webhookId } }),
    });

    if (!response.ok) {
      const body = await response.text().catch(() => '');
      console.error('[PluggableAPI] Webhook calculator failed:', response.status, body || response.statusText);
      return false;
    }

    const result = (await response.json()) as { response?: { result?: { usageAmount?: number } } };

    const usageAmount = result?.response?.result?.usageAmount;
    if (typeof usageAmount !== 'number') {
      console.error('[PluggableAPI] Webhook calculator returned no usageAmount:', result);
      return false;
    }

    setAppStore({ toolPrice: usageAmount });
    return true;
  } catch (error) {
    console.error('[PluggableAPI] Webhook calculator error:', error);
    return false;
  }
};

export const virtualTryOn = async (characterImageUrl: string, prompt: string, lookFolder: string, onProgress?: () => void): Promise<ImageGenerationResult> => {
  return pluggableApiService.virtualTryOn({ characterImageUrl, prompt, lookFolder, onProgress });
};

export const generateOutfit = async (
  characterImageUrl: string,
  wardrobeItems: Array<{ name: string; category: string; imageUrl?: string; prompt?: string; isUserUploaded?: boolean }>,
  gender: string,
  lookFolder: string,
  options?: {
    style?: { name: string; description?: string; referenceUrl?: string };
    pose?: { prompt?: string; referenceUrl?: string };
    background?: { prompt?: string; referenceUrl?: string };
    hairstyle?: { description: string; referenceUrl?: string };
    hairColor?: string;
    aspectRatio?: number | null;
    retouchPresets?: Array<{ categoryId: string; presets: RetouchPreset[] }>;
    onProgress?: () => void;
    onSubmitted?: (executionId: string) => void;
  },
): Promise<ImageGenerationResult> => {
  return pluggableApiService.generateOutfit({
    characterImageUrl,
    wardrobeItems,
    gender,
    lookFolder,
    style: options?.style,
    pose: options?.pose,
    background: options?.background,
    hairstyle: options?.hairstyle,
    hairColor: options?.hairColor,
    aspectRatio: options?.aspectRatio,
    retouchPresets: options?.retouchPresets,
    onProgress: options?.onProgress,
    onSubmitted: options?.onSubmitted,
  });
};

export const getOutfitSuggestions = async (characterDescription: string, occasion: string, style: string): Promise<ChatCompletionResult> => {
  return pluggableApiService.getOutfitSuggestions({ characterDescription, occasion, style });
};
